import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@iconify/react';
import { Survey } from '../features/surveys/types';

interface SurveyActionsDropdownProps {
  survey: Survey;
  onEdit: (survey: Survey) => void;
  onManageForms: (id: string) => void;
  onViewResponses: (id: string) => void;
  onViewStatistics: (id: string) => void;
  onViewTeableSheet: (id: string) => void;
  onShareLink: (survey: Survey) => void;
  onDelete: (id: string) => void;
  generatingToken?: boolean;
  showShareLink?: boolean;
  canEdit?: boolean;
}

export function SurveyActionsDropdown({
  survey,
  onEdit,
  onManageForms,
  onViewResponses,
  onViewStatistics,
  onViewTeableSheet,
  onShareLink,
  onDelete,
  generatingToken = false,
  showShareLink = true,
  canEdit = true,
}: SurveyActionsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUp: false });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollResize = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 280; // Altura aproximada del dropdown
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < dropdownHeight;

        setCoords({
          top: openUp 
            ? rect.top + window.scrollY - dropdownHeight - 8 
            : rect.bottom + window.scrollY,
          left: rect.right - 224 + window.scrollX,
          openUp
        });
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollResize);
      window.addEventListener('resize', handleScrollResize);
      // Recalcular posición inmediatamente al abrir
      handleScrollResize();
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollResize);
      window.removeEventListener('resize', handleScrollResize);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleAction = (action: () => void) => {
    action();
    setIsOpen(false);
  };

  const dropdownMenu = (
    <div 
      ref={dropdownRef}
      style={{ 
        position: 'absolute', 
        top: `${coords.top}px`, 
        left: `${coords.left}px`,
        zIndex: 9999 
      }}
      className={`w-56 rounded-md shadow-lg bg-card border border-border ring-1 ring-black ring-opacity-5 overflow-hidden animate-in fade-in zoom-in-95 duration-100 ${
        coords.openUp ? 'origin-bottom' : 'origin-top'
      }`}
    >
      <div className="py-1" role="menu" aria-orientation="vertical">
        {canEdit && (
          <>
            <button
              onClick={() => handleAction(() => onEdit(survey))}
              className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
              role="menuitem"
            >
              <Icon icon="mdi:pencil" className="w-4 h-4 mr-3 text-muted-foreground" />
              Editar Encuesta
            </button>
            
            <button
              onClick={() => handleAction(() => onManageForms(survey.id))}
              className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
              role="menuitem"
            >
              <Icon icon="mdi:form-select" className="w-4 h-4 mr-3 text-muted-foreground" />
              Gestionar Formularios
            </button>

            {showShareLink && (
              <>
                <div className="border-t border-border my-1"></div>
                <button
                  onClick={() => handleAction(() => onShareLink(survey))}
                  disabled={generatingToken}
                  className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left disabled:opacity-50"
                  role="menuitem"
                >
                  <Icon icon="mdi:share-variant" className="w-4 h-4 mr-3 text-muted-foreground" />
                  {generatingToken ? 'Generando...' : 'Compartir Link'}
                </button>
              </>
            )}

            <div className="border-t border-border my-1"></div>

            <button
              onClick={() => handleAction(() => onDelete(survey.id))}
              className="flex items-center w-full px-4 py-2 text-sm text-destructive hover:bg-destructive/10 transition-colors text-left"
              role="menuitem"
            >
              <Icon icon="mdi:delete" className="w-4 h-4 mr-3 text-destructive" />
              Eliminar
            </button>
          </>
        )}

        <button
          onClick={() => handleAction(() => onViewResponses(survey.id))}
          className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
          role="menuitem"
        >
          <Icon icon="mdi:table-eye" className="w-4 h-4 mr-3 text-muted-foreground" />
          Ver Respuestas
        </button>

        <button
          onClick={() => handleAction(() => onViewStatistics(survey.id))}
          className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
          role="menuitem"
        >
          <Icon icon="mdi:chart-bar" className="w-4 h-4 mr-3 text-muted-foreground" />
          Ver Estadísticas
        </button>

        <button
          onClick={() => handleAction(() => onViewTeableSheet(survey.id))}
          className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
          role="menuitem"
        >
          <Icon icon="mdi:microsoft-excel" className="w-4 h-4 mr-3 text-muted-foreground" />
          Ver Hoja Teable
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Icon icon="mdi:dots-vertical" className="w-5 h-5" />
      </button>

      {isOpen && createPortal(dropdownMenu, document.body)}
    </div>
  );
}
